import type { Shape, Slide } from "@/lib/types";

export type SummaryLine = {
  category:
    | "text"
    | "font"
    | "transform"
    | "fill"
    | "line"
    | "shape-added"
    | "shape-removed"
    | "meta";
  shape?: string;
  text: string;
};

const EMU_PER_CM = 360000;

function emuToCm(emu: number): string {
  return (emu / EMU_PER_CM).toFixed(1);
}

function truncate(s: string, n = 40): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n)}…`;
}

function extractText(shape: Shape): string {
  const tf = shape.text_frame as
    | { paragraphs?: Array<{ runs?: Array<{ text?: string }> }> }
    | null
    | undefined;
  if (!tf?.paragraphs) return "";
  return tf.paragraphs
    .map((p) => (p.runs ?? []).map((r) => r.text ?? "").join(""))
    .filter((s) => s.length > 0)
    .join(" / ");
}

function firstRunFont(shape: Shape): {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  color?: { kind?: string; value?: string; name?: string };
} | null {
  const tf = shape.text_frame as
    | {
        paragraphs?: Array<{
          runs?: Array<{ font?: Record<string, unknown> }>;
        }>;
      }
    | null
    | undefined;
  if (!tf?.paragraphs) return null;
  for (const p of tf.paragraphs) {
    for (const r of p.runs ?? []) {
      if (r.font) return r.font as ReturnType<typeof firstRunFont> extends infer X ? X : never;
    }
  }
  return null;
}

function extractSolidFillHex(shape: Shape): string | null {
  const fill = shape.fill as
    | { type?: string; color?: { kind?: string; value?: string; name?: string } }
    | null
    | undefined;
  if (!fill || fill.type !== "solid") return null;
  if (fill.color?.kind === "rgb" && typeof fill.color.value === "string") {
    return `#${fill.color.value.toUpperCase()}`;
  }
  if (fill.color?.kind === "theme" && typeof fill.color.name === "string") {
    return `theme/${fill.color.name}`;
  }
  return null;
}

function shapeLabel(shape: Shape): string {
  if (shape.type === "placeholder") {
    // Placeholder type is under shape.placeholder.ph_type
    const ph = (shape as unknown as { placeholder?: { ph_type?: string } }).placeholder;
    const phType = ph?.ph_type;
    if (phType === "title" || phType === "ctr_title") return "タイトル";
    if (phType === "body") return "本文";
    if (phType === "subtitle") return "サブタイトル";
    return `プレースホルダ(${phType ?? "?"})`;
  }
  if (shape.type === "picture") return `画像 (${shape.name || shape.id})`;
  if (shape.type === "autoShape") return `図形 (${shape.name || shape.id})`;
  if (shape.type === "textBox") return `テキストボックス (${shape.name || shape.id})`;
  return `${shape.type} (${shape.name || shape.id})`;
}

export function summarizeChanges(before: Slide, after: Slide): SummaryLine[] {
  const out: SummaryLine[] = [];

  if (before.notes !== after.notes) {
    out.push({
      category: "meta",
      text: `ノート: 「${truncate(before.notes || "(空)")}」→「${truncate(after.notes || "(空)")}」`,
    });
  }

  const beforeById = new Map(before.shapes.map((s) => [s.id, s]));
  const afterById = new Map(after.shapes.map((s) => [s.id, s]));

  for (const [id, afterShape] of afterById) {
    const beforeShape = beforeById.get(id);
    if (!beforeShape) {
      out.push({
        category: "shape-added",
        shape: id,
        text: `新しい ${shapeLabel(afterShape)} が追加されました`,
      });
      continue;
    }

    const beforeText = extractText(beforeShape);
    const afterText = extractText(afterShape);
    if (beforeText !== afterText) {
      out.push({
        category: "text",
        shape: id,
        text: `${shapeLabel(afterShape)}: テキスト「${truncate(beforeText) || "(空)"}」→「${truncate(afterText) || "(空)"}」`,
      });
    }

    const bt = beforeShape.transform;
    const at = afterShape.transform;
    if (bt.left !== at.left || bt.top !== at.top) {
      out.push({
        category: "transform",
        shape: id,
        text: `${shapeLabel(afterShape)}: 位置 (${emuToCm(bt.left)}, ${emuToCm(bt.top)}) → (${emuToCm(at.left)}, ${emuToCm(at.top)}) cm`,
      });
    }
    if (bt.width !== at.width || bt.height !== at.height) {
      out.push({
        category: "transform",
        shape: id,
        text: `${shapeLabel(afterShape)}: サイズ ${emuToCm(bt.width)}×${emuToCm(bt.height)} → ${emuToCm(at.width)}×${emuToCm(at.height)} cm`,
      });
    }
    if (bt.rotation !== at.rotation) {
      out.push({
        category: "transform",
        shape: id,
        text: `${shapeLabel(afterShape)}: 回転 ${bt.rotation}° → ${at.rotation}°`,
      });
    }

    const beforeFill = extractSolidFillHex(beforeShape);
    const afterFill = extractSolidFillHex(afterShape);
    if (beforeFill !== afterFill) {
      out.push({
        category: "fill",
        shape: id,
        text: `${shapeLabel(afterShape)}: 塗りつぶし ${beforeFill ?? "(なし)"} → ${afterFill ?? "(なし)"}`,
      });
    }

    const beforeFont = firstRunFont(beforeShape);
    const afterFont = firstRunFont(afterShape);
    if (beforeFont && afterFont) {
      if (beforeFont.name !== afterFont.name) {
        out.push({
          category: "font",
          shape: id,
          text: `${shapeLabel(afterShape)}: フォント ${beforeFont.name ?? "(既定)"} → ${afterFont.name ?? "(既定)"}`,
        });
      }
      if (beforeFont.size !== afterFont.size) {
        const beforePt = beforeFont.size ? (beforeFont.size / 100).toFixed(0) : "?";
        const afterPt = afterFont.size ? (afterFont.size / 100).toFixed(0) : "?";
        out.push({
          category: "font",
          shape: id,
          text: `${shapeLabel(afterShape)}: フォントサイズ ${beforePt}pt → ${afterPt}pt`,
        });
      }
      if (beforeFont.bold !== afterFont.bold) {
        out.push({
          category: "font",
          shape: id,
          text: `${shapeLabel(afterShape)}: 太字 ${beforeFont.bold ? "ON" : "OFF"} → ${afterFont.bold ? "ON" : "OFF"}`,
        });
      }
      if (beforeFont.italic !== afterFont.italic) {
        out.push({
          category: "font",
          shape: id,
          text: `${shapeLabel(afterShape)}: 斜体 ${beforeFont.italic ? "ON" : "OFF"} → ${afterFont.italic ? "ON" : "OFF"}`,
        });
      }
      const beforeColor =
        beforeFont.color?.kind === "rgb" && beforeFont.color.value
          ? `#${beforeFont.color.value.toUpperCase()}`
          : beforeFont.color?.kind === "theme" && beforeFont.color.name
            ? `theme/${beforeFont.color.name}`
            : null;
      const afterColor =
        afterFont.color?.kind === "rgb" && afterFont.color.value
          ? `#${afterFont.color.value.toUpperCase()}`
          : afterFont.color?.kind === "theme" && afterFont.color.name
            ? `theme/${afterFont.color.name}`
            : null;
      if (beforeColor !== afterColor) {
        out.push({
          category: "font",
          shape: id,
          text: `${shapeLabel(afterShape)}: 文字色 ${beforeColor ?? "(既定)"} → ${afterColor ?? "(既定)"}`,
        });
      }
    }
  }

  for (const [id, beforeShape] of beforeById) {
    if (!afterById.has(id)) {
      out.push({
        category: "shape-removed",
        shape: id,
        text: `${shapeLabel(beforeShape)} が削除されました`,
      });
    }
  }

  return out;
}
